import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { FaTrashAlt } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";

// ✅ Import Context
import { useMenu } from "../../context/MenuContext";

const calculateCartCount = (items) => {
  return items.reduce((sum, item) => {
    return sum + Number(item.quantity || 0);
  }, 0);
};

const API_URL = "https://tyka.premierhostings.com/beta/api/cart";

const CartDrawer = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);

  // ✅ Use global cart count manager
  const { fetchMenuCounts = () => {}, setCartCount = () => {} } =
    useMenu() || {};

  // Tokens
  const authToken = localStorage.getItem("authToken");
  const guestToken = localStorage.getItem("guest_token");
  const userId =
    localStorage.getItem("authUserId") || localStorage.getItem("guest_id");
  const token = authToken || guestToken;
  const isGuest = !authToken && !!guestToken;

  // ✅ Fetch Cart
  const fetchCart = async () => {
    try {
      if (!token) return;

      const url = isGuest ? `${API_URL}/?guest_token=${guestToken}` : API_URL;

      const res = await axios.get(url, {
        headers: isGuest
          ? { "Content-Type": "application/json" }
          : { Authorization: `Bearer ${authToken}` },
      });

      if (
        res.data &&
        Array.isArray(res.data.data) &&
        res.data.data.length > 0
      ) {
        const mergedItems = res.data.data.flatMap((cart) => cart.items || []);
        const withQty = mergedItems.map((item) => ({
          ...item,
          quantity: item.quantity || 1,
        }));
        const grouped = Object.values(
          withQty.reduce((acc, item) => {
            if (item.bundle) {
              const key = `bundle-${item.bundle.id}`;

              if (!acc[key]) {
                acc[key] = {
                  id: key,
                  isBundle: true,
                  bundle: item.bundle,
                  quantity: item.quantity || 1,
                  products: [],
                };
              }

              acc[key].products.push(item);
            } else {
              acc[`product-${item.id}`] = {
                ...item,
                isBundle: false,
              };
            }

            return acc;
          }, {}),
        );
        console.log("grouped", grouped);

        setCartItems(grouped);

        console.log("Grouped:", grouped);

        const count = calculateCartCount(grouped);

        console.log("Count:", count);

        setCartCount(count);
        setCartCount(calculateCartCount(grouped));
      } else {
        setCartItems([]);
        setCartCount(0);
      }
    } catch (error) {
      console.error("❌ Error fetching cart:", error);
    }
  };

  // ✅ Increase Quantity
  const handleIncrease = async (id) => {
    const item = cartItems.find((item) => item.id === id);
    if (!item) return;

    const newQuantity = item.quantity + 1;

    try {
      const url = isGuest
        ? `${API_URL}/${id}?guest_token=${guestToken}`
        : `${API_URL}/${id}`;

      await axios.put(
        url,
        {
          product_id: id,
          quantity: newQuantity,
        },
        {
          headers: isGuest
            ? { "Content-Type": "application/json" }
            : { Authorization: `Bearer ${authToken}` },
        },
      );

      const updated = cartItems.map((i) =>
        i.id === id ? { ...i, quantity: newQuantity } : i,
      );

      setCartItems(updated);
      setCartCount(calculateCartCount(updated));

      // Success par kuch nahi karna
    } catch (error) {
      console.error("Error increasing quantity:", error);

      // Sirf error par toast
      toast.error(error?.response?.data?.message || "Failed to update cart");
    }
  };

  // ✅ Decrease Quantity
  const handleDecrease = async (id) => {
    const item = cartItems.find((item) => item.id === id);
    if (!item || item.quantity <= 1) return;
    const newQuantity = item.quantity - 1;

    try {
      const url = isGuest
        ? `${API_URL}/${id}?guest_token=${guestToken}`
        : `${API_URL}/${id}`;

      await axios.put(
        url,
        { product_id: id, quantity: newQuantity },
        {
          headers: isGuest
            ? { "Content-Type": "application/json" }
            : { Authorization: `Bearer ${authToken}` },
        },
      );

      const updated = cartItems.map((i) =>
        i.id === id ? { ...i, quantity: newQuantity } : i,
      );
      setCartItems(updated);

      setCartCount(calculateCartCount(updated));
    } catch (error) {
      console.error("Error decreasing quantity:", error);
    }
  };

  // ✅ Delete Item
  const handleRemoveItem = async (id) => {
    try {
      const url = isGuest
        ? `${API_URL}/${id}?guest_token=${guestToken}`
        : `${API_URL}/${id}?user_id=${userId}`;

      await axios.delete(url, {
        headers: isGuest
          ? { "Content-Type": "application/json" }
          : { Authorization: `Bearer ${authToken}` },
      });

      const updated = cartItems.filter((item) => item.id !== id);

      setCartItems(updated);

      // Update global cart count
      const totalCount = updated.reduce(
        (sum, item) => sum + Number(item.quantity || 0),
        0,
      );

      setCartCount(totalCount);

      // Update menu counts
      fetchMenuCounts(userId);

      // Success toast
      toast.success("Item removed from cart!");
    } catch (error) {
      console.error("Error deleting item:", error);

      // Error toast
      toast.error(
        error?.response?.data?.message || "Failed to remove item from cart",
      );
    }
  };

  // ✅ Subtotal
  const subtotal = cartItems.reduce((total, item) => {
    if (item.isBundle) {
      return total + Number(item.bundle.offer_price) * item.quantity;
    }

    const price =
      typeof item.price === "object"
        ? parseFloat(item.price.amount || 0)
        : parseFloat(item.price || 0);

    return total + price * item.quantity;
  }, 0);

  useEffect(() => {
    if (isOpen) fetchCart();
  }, [isOpen]);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40"
          onClick={onClose}
        ></div>
      )}

      <div
        className={`fixed top-0 right-0 h-[80vh] w-full sm:w-[350px] md:w-[400px] lg:w-[430px] bg-white z-50 shadow-lg transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="font-bold text-sm uppercase">
              Shopping cart ({calculateCartCount(cartItems)})
            </h2>
            <button onClick={onClose}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Cart Items */}
          <div className="p-4 space-y-4">
            {cartItems.length > 0 ? (
              cartItems.map((item) => {
                if (item.isBundle) {
                  return (
                    <div key={item.id} className="border rounded-lg p-3">
                      <div className="flex justify-between items-start border-b pb-2 mb-3">
                        {/* Left */}
                        <div>
                          <h4 className="font-bold text-red-600">
                            Bundle: {item.bundle.name}
                          </h4>

                          <p className="text-xs line-through text-gray-400">
                            ₹{item.bundle.original_price}
                          </p>

                          <p className="font-bold text-red-600">
                            ₹{item.bundle.offer_price}
                          </p>
                        </div>

                        {/* Right */}
                        <div className="flex flex-col items-end gap-2">
                          <button
                            onClick={() => handleRemoveItem(item.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <FaTrashAlt />
                          </button>

                          <div className="flex items-center gap-2 border rounded-2xl px-2 py-1 bg-gray-200">
                            <button
                              className="text-lg px-2"
                              onClick={() => handleDecrease(item.id)}
                            >
                              −
                            </button>

                            <span>{item.quantity}</span>

                            <button
                              className="text-lg px-2"
                              onClick={() => handleIncrease(item.id)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {item.products.map((product) => (
                          <div
                            key={product.id}
                            className="flex gap-3 border-b pb-2 last:border-0"
                          >
                            <img
                              src={product.product.default_image}
                              className="w-16 h-16 rounded object-cover"
                              alt={product.product.name}
                            />

                            <div className="flex-1">
                              <h5 className="font-semibold">
                                {product.product.name}
                              </h5>

                              <p className="text-xs text-gray-500">
                                Color: {product.color?.name}
                              </p>

                              <p className="text-xs text-gray-500">
                                Size: {product.size?.name}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                const productName = item.product?.name || "Unnamed Product";
                const productImage = item.product?.default_image || "#";
                const price =
                  typeof item.price === "object"
                    ? parseFloat(item.price.amount || 0)
                    : parseFloat(item.price || 0);

                return (
                  <div key={item.id} className="flex gap-4 border-b pb-3">
                    <img
                      src={productImage}
                      alt={productName}
                      className="w-[60px] h-[80px] sm:w-[70px] sm:h-[90px] md:w-[80px] md:h-[100px] object-cover rounded"
                    />
                    <div className="flex-1 text-sm">
                      <h4 className="font-semibold text-sm sm:text-base md:text-base">
                        {productName}
                      </h4>

                      {item.size && (
                        <p className="text-gray-500">Size: {item.size?.name}</p>
                      )}
                      {item.color && (
                        <p className="text-gray-500">
                          Color: {item.color?.name}
                        </p>
                      )}

                      <div className="flex justify-between items-center mt-2">
                        <span className="font-semibold text-sm sm:text-base md:text-lg">
                          ₹{(price * item.quantity).toFixed(2)}
                        </span>
                        <div className="flex items-center gap-2 border rounded-2xl px-2 py-1 bg-gray-200 -mr-2 sm:-mr-4 md:-mr-6">
                          <button
                            className="text-lg px-2"
                            onClick={() => handleDecrease(item.id)}
                          >
                            −
                          </button>
                          <span>{item.quantity}</span>
                          <button
                            className="text-lg px-2"
                            onClick={() => handleIncrease(item.id)}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                    <button
                      className="self-start mt-2 sm:mt-0"
                      onClick={() => handleRemoveItem(item.id)}
                    >
                      <FaTrashAlt className="text-red-600 hover:text-red-800" />
                    </button>
                  </div>
                );
              })
            ) : (
              <p className="text-center text-gray-500">Your cart is empty</p>
            )}
          </div>

          {/* Summary & Actions */}
          <div className="p-4 border-t text-sm pb-6 mt-auto">
            <div className="flex justify-between mb-4">
              <span>{cartItems.length} items</span>
              <span className="font-bold text-red-600">
                ₹{subtotal.toFixed(2)}
              </span>
            </div>

            <div className="flex gap-3">
              <button
                className="w-1/2 bg-red-600 text-white py-2 rounded font-semibold hover:bg-red-700"
                onClick={() => {
                  navigate("/payment");
                  onClose();
                }}
              >
                Checkout
              </button>
              <button
                className="w-1/2 border border-gray-300 py-2 rounded font-semibold hover:bg-gray-100"
                onClick={() => {
                  navigate("/cart");
                  onClose();
                }}
              >
                View Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CartDrawer;
